import { TransportError } from 'ProdantixSdk/core/errors';
import { type FetchLike, makeAbortController, resolveFetch, trimTrailingSlash } from 'ProdantixSdk/core/http';

export type PushPlatform = 'android' | 'ios' | 'web';

export type PushPermission = 'denied' | 'granted' | 'provisional';

export interface PushDevice {
  appId: string;
  distinctId: string;
  permission?: PushPermission;
  platform: PushPlatform;
  /** The APNs or FCM token, or the browser's Push API subscription as JSON. */
  token: string;
}

export interface PushClientOptions {
  fetch?: FetchLike;
  host: string;
  projectKey: string;
  requestTimeoutMs?: number;
}

/** The edge's push routes: an app's Web Push key, a device's registration
 *  and the report that a person opened a notification. */
export class PushClient {
  private readonly fetch?: FetchLike;
  private readonly host: string;
  private readonly projectKey: string;
  private readonly requestTimeoutMs: number;

  constructor(options: PushClientOptions) {
    this.fetch = resolveFetch(options.fetch);
    this.host = trimTrailingSlash(options.host);
    this.projectKey = options.projectKey;
    this.requestTimeoutMs = options.requestTimeoutMs ?? 10_000;
  }

  private async request(path: string, init: { body?: string; method: 'GET' | 'POST' }): Promise<{ status: number; text: string }> {
    const fetchImpl = this.fetch;
    if (!fetchImpl) {
      throw new TransportError('no fetch implementation available in this runtime');
    }
    const controller = makeAbortController();
    const timer = controller ? setTimeout(() => controller.abort(), this.requestTimeoutMs) : undefined;
    try {
      const headers: Record<string, string> = { authorization: `Bearer ${this.projectKey}` };
      if (init.body !== undefined) {
        headers['content-type'] = 'application/json';
      }
      const response = await fetchImpl(`${this.host}${path}`, { body: init.body, headers, method: init.method, signal: controller?.signal });
      return { status: response.status, text: await response.text() };
    } finally {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    }
  }

  /** The public key a browser subscribes to the app's Web Push with. */
  async vapidKey(appId: string): Promise<string> {
    const { status, text } = await this.request(`/v1/push/vapid?appId=${encodeURIComponent(appId)}`, { method: 'GET' });
    if (status < 200 || status >= 300) {
      throw new TransportError(`push key request failed with status ${status}`, status);
    }
    return (JSON.parse(text) as { publicKey: string }).publicKey;
  }

  /** Registers a device for the person, answering its id. */
  async register(device: PushDevice): Promise<string> {
    const { status, text } = await this.request('/v1/push/devices', { body: JSON.stringify(device), method: 'POST' });
    if (status < 200 || status >= 300) {
      throw new TransportError(`push registration failed with status ${status}`, status);
    }
    return (JSON.parse(text) as { id: string }).id;
  }

  /** Reports the notification carrying `sendId` in its data as opened. */
  async opened(sendId: string): Promise<void> {
    const { status } = await this.request('/v1/push/opens', { body: JSON.stringify({ sendId }), method: 'POST' });
    if (status < 200 || status >= 300) {
      throw new TransportError(`push open report failed with status ${status}`, status);
    }
  }
}
